/**
 * Profile Photo API
 * @see profile-photo-api.md
 */

import { apiClient } from "./auth";

export interface ProfilePhotoRecord {
  id: string;
  tenantId: string;
  userId: string;
  originalName: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  s3Key: string;
  s3Bucket: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetProfilePhotoResponse {
  success: boolean;
  data: {
    photo: ProfilePhotoRecord;
    url: string;
    expiresAt: string;
  };
}

export interface UploadProfilePhotoResponse {
  success: boolean;
  data: ProfilePhotoRecord;
}

/**
 * Get current user's profile photo.
 * GET /auth/profile-photo
 */
export const getProfilePhotoApi = async (): Promise<GetProfilePhotoResponse | null> => {
  try {
    const response = await apiClient.get<GetProfilePhotoResponse>("/auth/profile-photo");
    return response.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return null;
    }
    throw err;
  }
};

/**
 * Get a tenant user's current profile photo by userId.
 * GET /auth/profile-photo/:userId
 */
export const getProfilePhotoByUserIdApi = async (
  userId: string
): Promise<GetProfilePhotoResponse | null> => {
  try {
    const response = await apiClient.get<GetProfilePhotoResponse>(
      `/auth/profile-photo/${encodeURIComponent(userId)}`
    );
    return response.data;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return null;
    }
    throw err;
  }
};

// Shared in-memory cache so repeated userIds are fetched once.
const profilePhotoUrlCacheByUserId = new Map<string, string | null>();
const profilePhotoInFlightByUserId = new Map<string, Promise<string | null>>();

export const getCachedProfilePhotoUrlByUserId = async (
  userId: string
): Promise<string | null> => {
  const normalizedUserId = (userId ?? "").trim();
  if (!normalizedUserId) return null;

  if (profilePhotoUrlCacheByUserId.has(normalizedUserId)) {
    return profilePhotoUrlCacheByUserId.get(normalizedUserId) ?? null;
  }

  const existingReq = profilePhotoInFlightByUserId.get(normalizedUserId);
  if (existingReq) return existingReq;

  const req = getProfilePhotoByUserIdApi(normalizedUserId)
    .then((res) => res?.data?.url ?? null)
    .catch(() => null)
    .then((url) => {
      profilePhotoUrlCacheByUserId.set(normalizedUserId, url);
      return url;
    })
    .finally(() => {
      profilePhotoInFlightByUserId.delete(normalizedUserId);
    });

  profilePhotoInFlightByUserId.set(normalizedUserId, req);
  return req;
};

/**
 * Upload profile photo for the logged-in user.
 * POST /auth/profile-photo (multipart/form-data, field: photo)
 * @param file - JPEG, PNG, or WebP. Max 5MB.
 */
export const uploadProfilePhotoApi = async (
  file: File
): Promise<UploadProfilePhotoResponse> => {
  const formData = new FormData();
  formData.append("photo", file);
  const response = await apiClient.post<FormData, UploadProfilePhotoResponse>(
    "/auth/profile-photo",
    formData
  );
  return response.data;
};
